import React, { useEffect, useState } from 'react';
import TopNavigation from '../components/TopNavigation';
import MobileContainer from '../components/MobileContainer';
import Card from '../components/Card';
import { auth } from '../firebase';
import { createStory, listStories } from '../utils/secureFunctions';
import { getMyPhoto } from '../utils/myProfile';
import { compressVideo, isVideoFile } from '../utils/videoCompression';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import { describeModerationError } from '../utils/moderationErrors';
import ImageWithFallback from '../components/ImageWithFallback';
import SignedVideo from '../components/SignedVideo';
import UserLink from '../components/UserLink';


interface Story {
  id: string;
  userId: string;
  userName: string;
  userPhoto: string;
  mediaURL: string;
  mediaType: 'image' | 'video';
  createdAt: any;
  expiresAt: any;
}

type UploadTaskHandle = { cancel: () => boolean };

const Stories: React.FC = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [currentTask, setCurrentTask] = useState<UploadTaskHandle | null>(null);


  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await listStories({ limit: 100 });
        if (!mounted) return;
        setStories(res.stories as any);
      } catch (e) {
        console.error('Failed to load stories', e);
      }
    };
    load();
    const t = window.setInterval(load, 20000);
    return () => {
      mounted = false;
      window.clearInterval(t);
    };
  }, []);

  useEffect(() => {
    // Revoke preview when file changes/unmounts
    return () => {
      if (previewURL) URL.revokeObjectURL(previewURL);
    };
  }, [previewURL]);

  const handleSelect: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const f = e.target.files?.[0] || null;
    if (previewURL) { URL.revokeObjectURL(previewURL); setPreviewURL(null); }
    if (!f) { setFile(null); return; }

    // Client-side size guard (10MB images, 100MB videos)
    const isVideo = isVideoFile(f);
    const maxImage = 10 * 1024 * 1024;
    const maxVideo = 100 * 1024 * 1024;
    let chosen: File = f;

    if (isVideo) {
      if (f.size > maxVideo) {
        // Try compressing to get under 100MB
        try {
          chosen = await compressVideo(f, {}, () => {});
          if (chosen.size > maxVideo) {
            alert('Video is too large for Stories (max 100MB) even after optimization. Please trim/compress and try again.');
            setFile(null); return;
          }
        } catch (err) {
          alert('Failed to optimize video. Please try a smaller file (max 100MB).');
          setFile(null); return;
        }
      }
    } else {
      if (f.size > maxImage) {
        alert('Image is too large for Stories (max 10MB). Please choose a smaller image.');
        setFile(null); return;
      }
    }

    setFile(chosen);
    try {
      const url = URL.createObjectURL(chosen);
      setPreviewURL(url);
    } catch {}
  };

  const handleClear = () => {
    setFile(null);
    if (previewURL) URL.revokeObjectURL(previewURL);
    setPreviewURL(null);
  };

  const handleUpload = async () => {
    if (!auth.currentUser || !file) return;
    setUploading(true);
    try {
      // Validate type and size
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        alert('Only image or video files are allowed.');
        setUploading(false);
        return;
      }

      if (isImage && file.size > 10 * 1024 * 1024) {
        alert('Image too large. Max 10MB.');
        setUploading(false);
        return;
      }

      let uploadFile: File = file;
      if (isVideo) {
        // If >50MB, try to compress to fit under 100MB rule
        if (file.size > 50 * 1024 * 1024) {
          try {
            uploadFile = await compressVideo(file);
          } catch (err) {
            console.warn('Compression failed, uploading original video', err);
          }
        }
        if (uploadFile.size > 100 * 1024 * 1024) {
          alert('Video too large (max 100MB). Try trimming or a shorter clip.');
          setUploading(false);
          return;
        }
      }

      const inferType = (name: string): string => {
        const ext = name.split('.').pop()?.toLowerCase() || '';
        if (['jpg','jpeg','png','webp','bmp'].includes(ext)) {
          const normalized = ext === 'jpg' ? 'jpeg' : ext;
          return `image/${normalized}`;
        }
        if (['mp4','mov','webm','mkv','ogg','ogv','m4v'].includes(ext)) {
          if (ext === 'mov') return 'video/quicktime';
          if (ext === 'm4v') return 'video/x-m4v';
          if (ext === 'ogg' || ext === 'ogv') return 'video/ogg';
          return `video/${ext}`;
        }
        return isImage ? 'image/jpeg' : (isVideo ? 'video/mp4' : 'application/octet-stream');
      };
      const contentType = uploadFile.type && uploadFile.type.trim().length > 0 ? uploadFile.type : inferType(uploadFile.name);
      setProgress(0);
      const upload = await uploadModeratedMedia({
        folder: 'stories',
        field: 'mediaURL',
        file: uploadFile,
        contentType,
        onProgress: (pct) => setProgress(pct),
      });
      const mediaURL = upload.objectKey;
      const mediaType: 'image' | 'video' = uploadFile.type.startsWith('video') ? 'video' : 'image';
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);
      await createStory({
        mediaURL,
        mediaType,
        moderationTokens: { mediaURL: upload.proof },
        expiresAt: expiresAt.toISOString(),
        userName: auth.currentUser.displayName || 'Anonymous',
        userPhoto: (await getMyPhoto()),
      });
      setFile(null);
      setProgress(0);
      if (previewURL) {
        URL.revokeObjectURL(previewURL);
        setPreviewURL(null);
      }
      alert('Story uploaded');
    } catch (e: any) {
      console.error(e);
      const friendly = describeModerationError(e);
      if (friendly) alert(friendly);
      // Legacy cloud-storage error codes are not applicable in the R2 flow.
      else alert('Failed to upload story. Ensure file type/size fits (images <10MB, videos <100MB).');
    } finally {
      setUploading(false);
      setCurrentTask(null);
    }
  };
  const handleCancelUpload = () => {
    try {
      currentTask?.cancel();
    } catch {}
    setUploading(false);
    setProgress(0);
    setCurrentTask(null);
  };

  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div style={{ background: 'var(--color-background-alt)', minHeight: '100vh' }}>
      <TopNavigation />
      <MobileContainer maxWidth={1200} paddingDesktop={20} paddingMobile={12}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, marginBottom: 4, fontSize: 32, color: '#072B2C' }}>Stories</h1>
            <p style={{ margin: 0, color: '#5B6E6E', fontSize: 14 }}>Share moments that disappear in 24 hours</p>
          </div>
        </div>
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <input
              type="file"
              accept="image/*,video/*"
              onChange={handleSelect}
            />
            {file && !uploading && (
              <button onClick={handleClear} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fafafa', cursor: 'pointer' }}>Remove</button>
            )}
            <button
              disabled={uploading || !file}
              onClick={handleUpload}
              style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: '#17A2A9', color: '#fff', cursor: uploading || !file ? 'not-allowed' : 'pointer', opacity: uploading || !file ? 0.6 : 1 }}
            >
              {uploading ? `Uploading ${Math.round(progress)}%` : 'Add to Story'}
            </button>
            {uploading && (
              <button onClick={handleCancelUpload} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>
                Cancel
              </button>
            )}
          </div>

          {previewURL && (
            <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden', background: '#000' }}>
              {file?.type.startsWith('video') ? (
                <video src={previewURL} style={{ width: '100%', maxHeight: 320, objectFit: 'contain' as const }} controls muted />
              ) : (
                <img src={previewURL} alt="preview" style={{ width: '100%', maxHeight: 320, objectFit: 'contain' as const }} />
              )}
            </div>
          )}
          {uploading && (
            <div style={{ marginTop: 8 }}>
              <div style={{ height: 8, background: '#eee', borderRadius: 9999, overflow: 'hidden' }}>
                <div style={{ width: `${progress}%`, height: '100%', background: '#17A2A9', transition: 'width .2s ease' }} />
              </div>
            </div>
          )}
        </Card>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {stories.map((s) => (
            <div key={s.id} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              {s.mediaType === 'video' ? (
                <SignedVideo src={s.mediaURL} controls style={{ width: '100%', height: 280, objectFit: 'cover' }} />
              ) : (
                <ImageWithFallback src={s.mediaURL} alt="story" style={{ width: '100%', height: 280, objectFit: 'contain', background: '#000' }} />
              )}
              <div style={{ padding: 10 }}>
                <UserLink userId={s.userId} as="div" style={{ fontWeight: 600 }}>{s.userName}</UserLink>
                <div style={{ color: '#5B6E6E', fontSize: 12 }}>Expires soon</div>
              </div>
            </div>
          ))}
        </div>
      </MobileContainer>
    </div>
  );
};

export default Stories;






